import { useLayoutEffect, useState } from "react";

// Tracks the live width of the table's scroll container (the div
// AutoFitContentInScreen sizes via screenSizeForTable) so column widths can
// be fitted to it. Reacts to real layout changes only - window resizes and
// nav bar collapse/expand - not to anything the virtualiser does while
// scrolling, since the container's own width is untouched by that.
//
// useLayoutEffect, not useEffect: the latter runs after the browser paints,
// so the first render after isLoading clears would paint with width still 0
// (content-only column widths, sized with no container to fit) and only
// correct itself a frame later once the effect measures the real element.
// Confirmed the gap is real and large enough to see - on a ~1280px window
// with realistic task data, that first paint computed to ~1588px against a
// 1280px container, a visible ~300px overflow (and matching scrollbar) that
// disappeared as soon as the true width landed. useLayoutEffect measures
// synchronously before the paint, so the browser never shows that frame.
//
// `remountKey` should be a value that changes at the point the ref's target
// actually mounts (e.g. the loading flag that gates an early-return spinner
// in the caller). A plain `[ref]` dependency isn't enough: ModelTaskTable
// calls this hook unconditionally but returns a spinner instead of the real
// tree while loading, so ref.current is still null on that first effect run
// - and since the ref object's own identity never changes, nothing would
// ever re-run this effect once the real element mounts.
const useContainerWidth = (ref, remountKey) => {
	const [width, setWidth] = useState(0);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element || typeof ResizeObserver === "undefined") return;
		// clientWidth (not entry.contentRect, and not getBoundingClientRect)
		// specifically because it excludes the vertical scrollbar this
		// container shows once there are enough rows - using a wider number
		// here is what let columns overflow the actual available space.
		const observer = new ResizeObserver(() => setWidth(element.clientWidth));
		observer.observe(element);
		setWidth(element.clientWidth);
		return () => observer.disconnect();
	}, [ref, remountKey]);

	return width;
};

export default useContainerWidth;
